/**
 * /pricing — dedicated pricing page with the full tier comparison.
 * Moved off the homepage on 2026-07-26 as part of the density cleanup.
 */
import { useEffect } from "react";
import { Sparkles, Repeat, Building2 } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import KickstartPricing from "@/components/sections/KickstartPricing";
import Pricing from "@/components/sections/Pricing";
import SubscriptionFeatureMatrix from "@/components/sections/SubscriptionFeatureMatrix";
import PricingComparisonTables from "@/components/sections/PricingComparisonTables";
import EnterpriseSection from "@/components/sections/EnterpriseSection";
import Comparison from "@/components/sections/Comparison";
import PricingFaq from "@/components/sections/PricingFaq";
import FoundingMembers from "@/components/sections/FoundingMembers";
import { PresaleDialogProvider } from "@/components/sections/PresaleDialog";
import { Link } from "react-router-dom";

export default function PricingPage() {
  useEffect(() => {
    document.title = "Pricing — Zynthoro | Lifetime deals from €79 · Subscriptions from €499/mo";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta(
      "description",
      "Zynthoro pricing — Kickstart lifetime deals from €79, monthly plans from €499, one-time credit additions from €24.99, and Enterprise plans. Compare every tier side-by-side."
    );

    const els = document.querySelectorAll(".zy-reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); }
      }),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <PresaleDialogProvider>
      <Navbar />
      <main data-testid="page-pricing">
        <section className="zy-section bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="zy-eyebrow mb-4">Pricing</p>
                <h1 className="zy-h1">Simple, honest, European pricing.</h1>
                <p className="zy-body mt-6 max-w-lg">
                  Lifetime deals from €79, transparent monthly tiers, and an Enterprise plan for teams
                  that outgrow SaaS. Cancel any subscription any time. No lock-in, no surprise seat fees.
                </p>
              </div>
              <div>
                <div
                  className="rounded-2xl border border-[#eef1f7] p-6 sm:p-8"
                  style={{ background: "linear-gradient(160deg, #f7f9ff 0%, #eef2ff 100%)" }}
                >
                  <div className="space-y-3">
                    {[
                      { icon: Sparkles,  label: "Kickstart",     price: "from €79",    unit: "· lifetime", color: "#202B60" },
                      { icon: Repeat,    label: "Subscriptions", price: "from €499",   unit: "/ mo",       color: "#202B60" },
                      { icon: Building2, label: "Enterprise",    price: "Custom",      unit: "annual",     color: "#D4AF37" },
                    ].map((c) => {
                      const Icon = c.icon;
                      return (
                        <div
                          key={c.label}
                          className="flex items-center gap-4 rounded-xl bg-white p-4 shadow-sm"
                        >
                          <span
                            className="inline-flex items-center justify-center rounded-lg shrink-0"
                            style={{ background: `${c.color}14`, width: 40, height: 40 }}
                          >
                            <Icon size={18} style={{ color: c.color }} />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11.5px] font-semibold tracking-[0.14em] uppercase text-[#0A1628]/60">
                              {c.label}
                            </p>
                            <p className="mt-0.5 text-[14px] font-semibold text-[#0A1628]">
                              {c.price} <span className="text-[12.5px] font-normal text-[#0A1628]/55">{c.unit}</span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <KickstartPricing />
        <Pricing />
        <SubscriptionFeatureMatrix />
        <PricingComparisonTables />
        <EnterpriseSection />
        <Comparison />
        <section className="py-10 bg-[#FAFAFB]" aria-label="Vendor comparisons">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#0A1628]/60 mb-3">
              Compare Zynthoro
            </p>
            <div className="inline-flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/vs/zynthoro-vs-odoo"
                data-testid="pricing-vs-odoo-link"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#eef1f7] bg-white px-5 py-2 text-[13.5px] font-semibold text-[#0A1628] hover:border-[#202B60] hover:text-[#202B60] transition-colors"
              >
                Zynthoro vs Odoo
              </Link>
              <Link
                to="/vs/zynthoro-vs-netsuite"
                data-testid="pricing-vs-netsuite-link"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#eef1f7] bg-white px-5 py-2 text-[13.5px] font-semibold text-[#0A1628] hover:border-[#202B60] hover:text-[#202B60] transition-colors"
              >
                Zynthoro vs NetSuite
              </Link>
            </div>
          </div>
        </section>
        <FoundingMembers />
        <PricingFaq />
        <section className="py-8 bg-white">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <p className="text-[13px] text-slate-500" data-testid="pricing-affiliate-line">
              Running a community or channel around SMB software?{" "}
              <Link
                to="/affiliate"
                data-testid="pricing-affiliate-link"
                className="text-[#202B60] font-semibold hover:underline"
              >
                Affiliate — apply
              </Link>
              . Starter to Agency plans only. Kickstart excluded.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </PresaleDialogProvider>
  );
}
